/* eslint-disable import/prefer-default-export */
/**
 * Whitelabel Config Selectors
 * Memoized selectors for accessing whitelabel configuration data
 */

import type { ReduxStoreRootState } from '@/store/store.types';

/**
 * Select the entire whitelabel configuration from Redux state.
 * @param {object} state - Redux state.
 * @returns {object} Whitelabel configuration object.
 */
function selectWhitelabelConfig(state) {
  return state.whitelabelConfig || {};
}

/**
 * Select API configuration from whitelabel config state
 * Returns the entire API configuration object
 *
 * @param {Object} state - Redux state
 * @returns {Object} API configuration with purchaseUrl, searchUrl, mobileKey, desktopKey, etc.
 */
function selectWhitelabelApi(state) {
  return state.whitelabelConfig?.env?.api || {};
}

/**
 * Select entire whitelabel environment configuration
 *
 * @param {Object} state - Redux state
 * @returns {Object} Environment configuration
 */
function selectWhitelabelEnvs(state) {
  return state.whitelabelConfig?.env || {};
}

/**
 * Select the brand's configured date format for the search widget.
 *
 * A string, deliberately, and not the `env` object around it: `selectWhitelabelEnvs` falls back to
 * a fresh `{}` when there is no config yet, so a component subscribing to the object re-renders on
 * every dispatch until the config lands. A primitive compares by value and cannot do that.
 *
 * @param {Object} state - Redux state
 * @returns {string|undefined} The brand's format, if it configures one
 */
function selectSearchWidgetDateFormat(state) {
  return state.whitelabelConfig?.env?.searchWidget?.dateFormat;
}

/**
 * Select initialized features by reference, including canonical defaults and tenant overrides.
 */
function selectWhitelabelFeatures(state: ReduxStoreRootState) {
  return state.whitelabelConfig.features;
}

/**
 * Select whitelabel copies (translations/strings)
 *
 * @param {Object} state - Redux state
 * @returns {Object} Copies configuration
 */
function selectWhitelabelCopies(state) {
  return state.whitelabelConfig?.copies || {};
}

/**
 * Select whitelabel theme configuration
 *
 * @param {Object} state - Redux state
 * @returns {Object} Theme configuration with icons, colors, styles, etc.
 */
function selectWhitelabelTheme(state) {
  return state.whitelabelConfig?.env?.theme || {};
}

export {
  selectWhitelabelConfig,
  selectWhitelabelApi,
  selectWhitelabelEnvs,
  selectSearchWidgetDateFormat,
  selectWhitelabelFeatures,
  selectWhitelabelCopies,
  selectWhitelabelTheme,
};
